import { useEffect, useId, useState } from 'react'
import { X } from 'lucide-react'
import { Avatar, IconButton } from '@/components/play'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import { cn } from '@/lib/utils'
import type { AthletePosition, EventAthlete } from '@/lib/watch/eventAthletes'
import './athlete-card.css'

export interface AthleteCardProps {
  athlete: EventAthlete
  /** Their newest fix, or null before their tracker has reported (the host's preview). */
  position: AthletePosition | null
  /** Their race's name and colour (the course's), when they have one. */
  race: { name: string; color: string } | null
  stale: boolean
  /** Now, in epoch milliseconds: the age is read from it. */
  now: number
  /** Puts the card away (Close and Escape). Left out where its surface closes it, as in the host's preview. */
  onClose?: () => void
  /** `plain` drops the card's own surface, inside a surface of its own. */
  variant?: 'default' | 'plain'
}

/**
 * An athlete picked on the map (docs/features/athletes-and-trackers.md, Map): who they are and how old their position
 * is. A stale position says so in words ("Last seen"), not only by the dimmed dot. Escape or Close puts it away. The
 * host's Athletes view previews the same card. Their photo grows while a pointer is over the card so their face is
 * clear; a press on it keeps it large (or small again), which is how touch and the keyboard enlarge it.
 */
export function AthleteCard({ athlete, position, race, stale, now, onClose, variant = 'default' }: AthleteCardProps) {
  const titleId = useId()
  useEffect(() => {
    if (!onClose) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const age = position ? formatAge(now - position.recordedAt) : null
  const [enlarged, setEnlarged] = useState(false)
  const avatar = <Avatar name={athlete.name} src={athlete.photoUrl} size="compact" tone="auto" toneKey={athlete.id} />

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      className={cn('lv-athlete-card', variant === 'plain' && 'lv-athlete-card--plain', athlete.photoUrl && 'lv-athlete-card--photo')}
      data-enlarged={enlarged || undefined}
    >
      {athlete.photoUrl ? (
        <button type="button" className="lv-athlete-card__photo" aria-label="Enlarge photo" aria-pressed={enlarged} onClick={() => setEnlarged((was) => !was)}>
          {avatar}
        </button>
      ) : (
        avatar
      )}
      <div className="lv-athlete-card__body">
        <p id={titleId} className="lv-athlete-card__name">
          {athlete.name}
        </p>
        {(athlete.bib || race) && (
          <p className="lv-athlete-card__meta">
            {athlete.bib && <span>Bib {athlete.bib}</span>}
            {race && (
              <span className="lv-athlete-card__race">
                <span className="lv-athlete-card__swatch" style={{ background: race.color }} aria-hidden />
                {race.name}
              </span>
            )}
          </p>
        )}
        <p className="lv-athlete-card__age" data-stale={stale || undefined}>
          {age === null ? 'Not seen yet' : stale ? `Last seen ${age}` : `Seen ${age}`}
        </p>
      </div>
      {onClose && (
        <IconButton label="Close" size="compact" variant="ghost" onClick={onClose}>
          <X aria-hidden />
        </IconButton>
      )}
    </div>
  )
}
